/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@mui/material';
import {
  AccordionDetailsRoot,
  AccordionRoot,
  AccordionSummaryRoot,
  BadgeTemplateContainer,
  CheckboxGroup,
  CheckboxLabel,
  CheckboxRow,
  CloseButton,
  CommentsContainer,
  CommentsItems,
  CommentsTitle,
  Container,
  ContentRow,
  CustomCheckbox,
  DialogActionsStyled,
  DialogContentStyled,
  DialogPaper,
  DialogTitleStyled,
  Disregards,
  Footer,
  Question,
  ReportInaccurate,
  ScrollArea,
  StudentReportLabel,
  TextArea,
  Title
} from './styles/ReviewQuestionsStyle';
import Icons from '../icons/Icon';
import CheckboxOutlineIcon from '../icons/CheckboxOutlineIcon';
import CheckboxCheckedIcon from '../icons/CheckboxCheckedIcon';
import { BadgeTemplate } from '../tabular/components/Template';
import CustomScrollbar from '../CustomScrollbar/CustomScrollbar';

const ReviewQuestionModal = (props) => {
  const { open, onClose, reviewData, onInaccurateClick, textInfo, reportQuestionReasons } = props;

  const [expandedComments, setExpandedComments] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [comment, setComment] = useState('');

  if (!reviewData) return null;

  const handleCheckboxChange = (item) => {
    setSelectedReasons((prev) => {
      const alreadySelected = prev.includes(item.id);

      if (alreadySelected) {
        return prev.filter((id) => id !== item.id);
      }

      return [...prev, item.id];
    });
  };

  const handleSubmit = () => {
    const reasonForDisLike = selectedReasons.map((id) => {
      const reason = reportQuestionReasons.find((r) => r.id === id);

      return reason != null ? reason.value : id;
    });
    onInaccurateClick({
      questionId: reviewData.question.id,
      comments: comment,
      reasonForDisLike,
      ...(reviewData.feedbackSubmit || {})
    });

    setSelectedReasons([]);
    setComment('');
    onClose();
  };

  const resetForm = () => {
    setSelectedReasons([]);
    setComment('');
    onClose();
  };

  const formatTopic = (key) => {
    const result = key
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .trim();

    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  const formatStatus = (key) => key.toLowerCase();

  const reportBreakdown = reviewData?.reportBreakdown || {};
  const hasReportBreakdown = Object.values(reportBreakdown).some(
    (count) => Number(count) > 0
  );
  const hasStudentComments = (reviewData.studentComments?.length ?? 0) > 0;
  const showStudentReportsSection =
    Number(reviewData.totalStudentReports) > 0
    || hasReportBreakdown
    || hasStudentComments;

  const canSubmitReport =
    selectedReasons.length > 0 || comment.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperComponent={DialogPaper}
    >
      <CloseButton onClick={onClose}>
        <Icons name="CloseIcon" />
      </CloseButton>

      <Container>
        <DialogTitleStyled disableTypography>
          <Title>
            {textInfo.reportQuestionTitle}
          </Title>
        </DialogTitleStyled>

        <Question>
          {textInfo.reportQuestionLabel}
        </Question>

        <DialogContentStyled>
          <ContentRow>
            <CheckboxGroup>
              {reportQuestionReasons?.map((item) => (
                <CheckboxRow key={item.id}>
                  <CustomCheckbox
                    color="primary"
                    disableRipple
                    checked={selectedReasons.includes(item.id)}
                    onChange={() => handleCheckboxChange(item)}
                    icon={<CheckboxOutlineIcon />}
                    checkedIcon={<CheckboxCheckedIcon />}
                  />
                  <CheckboxLabel>
                    {item.label}
                  </CheckboxLabel>
                </CheckboxRow>
              ))}
            </CheckboxGroup>

            <TextArea
              rows={4}
              placeholder={textInfo.placeholderText}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </ContentRow>
        </DialogContentStyled>

        {showStudentReportsSection && (
          <div>
            <StudentReportLabel component="div">
              {reviewData.totalStudentReports} {textInfo.studentReports}
            </StudentReportLabel>

            <BadgeTemplateContainer>
              {Object.entries(reportBreakdown).map(([key, count]) => (
                <BadgeTemplate
                  key={key}
                  topics={[formatTopic(key)]}
                  status={formatStatus(key)}
                  count={count}
                />
              ))}
            </BadgeTemplateContainer>

            <AccordionRoot
              expanded={expandedComments}
              onChange={() => setExpandedComments(!expandedComments)}
              elevation={0}
            >
              <AccordionSummaryRoot
                expandIcon={
                  <Icons
                    name="ExpandIcon"
                    style={{ width: 16, height: 9 }}
                  />
                }
                aria-controls="student-comments-content"
                id="student-comments-header"
              >
                <CommentsTitle>
                  {textInfo.showComments}
                </CommentsTitle>
              </AccordionSummaryRoot>

              <AccordionDetailsRoot>
                <CommentsContainer>
                  <ScrollArea>
                    <CustomScrollbar>
                      {reviewData.studentComments?.map((comments, idx) => (
                        <CommentsItems key={idx}>
                          {comments}
                        </CommentsItems>
                      ))}
                    </CustomScrollbar>
                  </ScrollArea>
                </CommentsContainer>
              </AccordionDetailsRoot>
            </AccordionRoot>
          </div>
        )}

        <DialogActionsStyled>
          <Footer>
            <Disregards
              variant="outlined"
              onClick={resetForm}
            >
              {textInfo.cancelButtonLabel}
            </Disregards>

            <ReportInaccurate
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmitReport}
            >
              {textInfo.reportAsInaccurateButtonText}
            </ReportInaccurate>
          </Footer>
        </DialogActionsStyled>
      </Container>
    </Dialog>
  );
};

ReviewQuestionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInaccurateClick: PropTypes.func.isRequired,
  reviewData: PropTypes.object.isRequired,
  reportQuestionReasons: PropTypes.array.isRequired,
  textInfo: PropTypes.shape({
    commentsFromStudentLabel: PropTypes.string,
    cancelButtonLabel: PropTypes.string,
    reportAsInaccurateButtonText: PropTypes.string,
    flaggedText: PropTypes.string,
    showComments: PropTypes.string,
    studentReports: PropTypes.string,
    placeholderText: PropTypes.string,
    reportQuestionTitle: PropTypes.string,
    reportQuestionLabel: PropTypes.string
  }).isRequired
};

export default ReviewQuestionModal;
